/**
 * Reference-churn guards for the shared account/office state.
 *
 * Redux holds the account list, the current account, the office list and the
 * current office. Several dozen components subscribe to those, and a handful of
 * data hooks key their effects on the objects rather than on ids. That is fine
 * as long as a reducer only writes when something has actually changed - and it
 * was not: every consumer of `useUserAccounts` rebuilt the list (a fresh `.map`,
 * with a `Date.now()` cache-buster on the logo) and dispatched its own copy, so
 * one page load produced one new array reference per mounting component. Each
 * new reference re-ran the effects downstream of it, and because the API dedupe
 * only covers calls that are still in flight, every one of those re-runs became
 * a new HTTP request - which is how a dashboard ends up issuing the same
 * aggregate a few dozen times in a chained waterfall.
 *
 * These compare by identity content instead of by reference, so a dispatch that
 * would write back what is already there can be skipped and the churn stops at
 * the source.
 */

/** Two entities are the same row when they carry the same id. */
export const sameId = (a?: { id?: string } | null, b?: { id?: string } | null): boolean => {
  if (!a || !b) return !a && !b;
  return !!a.id && a.id === b.id;
};

/**
 * Two lists are the same list when they hold the same ids in the same order.
 *
 * Deliberately shallow, for the cases where identity is the whole question.
 * Where a field edit inside a row has to be caught as well - a renamed office,
 * say - use `sameContent`.
 */
export const sameIdList = (a?: { id?: string }[] | null, b?: { id?: string }[] | null): boolean => {
  if (!a || !b) return !a && !b;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]?.id !== b[i]?.id) return false;
  }
  return true;
};

/**
 * Value equality for a small derived list.
 *
 * The office list is rebuilt from the account on every pass, so comparing by
 * reference always says "changed" and comparing by id alone would miss a rename
 * or a deactivation. These lists are tens of rows, and this only runs when the
 * account itself changed, so a serialise-and-compare is the honest test and
 * cheap enough at that size.
 */
export const sameContent = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a === undefined || b === undefined) return false;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    // A cyclic or otherwise unserialisable value cannot be proved unchanged, so
    // it is treated as changed and the write goes through.
    return false;
  }
};
